import { useEffect, useState } from 'react'
import { EnvProvider, EnvSelector, useEnv } from './core/env/EnvContext'
import { ActivityLogProvider, ActivityLogPanel } from './core/ui/ActivityLog'
import { TokenProvider, useActiveTokens } from './core/auth/TokenProvider'
import { roleLabel } from './core/auth/roles'
import { MODULES } from './core/modules/registry'
import { useMe } from './core/auth/useMe'

export default function App() {
  return (
    <EnvProvider>
      <ActivityLogProvider>
        <TokenProvider>
          <Shell />
        </TokenProvider>
      </ActivityLogProvider>
    </EnvProvider>
  )
}

function Shell() {
  const me = useMe()
  const { environment } = useEnv()
  const [activeId, setActiveId] = useState(MODULES[0]?.id)
  const active = MODULES.find((m) => m.id === activeId) ?? MODULES[0]
  const ActiveView = active.View

  // The whole console is tinted by the working environment (data-env → --accent).
  return (
    <div className="app" data-env={environment}>
      <header className="topbar">
        <div className="brand">
          <span className="mark" aria-hidden="true" />
          <span className="brand-name">AssetSales</span>
          <span className="brand-sub">Dev Toolbox</span>
        </div>
        <div className="topbar-state">
          <EnvSelector />
          <TokenStatus />
          <UserChip me={me} />
        </div>
      </header>

      {me && !me.authenticated && (
        <div className="auth-warning">
          <strong>Not signed in.</strong> In production the Launchpad gateway provides your identity.
          For local dev, set <code>DEV_USER_SUB</code> in <code>.dev.vars</code>.
        </div>
      )}

      <div className="layout">
        <nav className="sidenav" aria-label="Modules">
          <div className="rail-label">Modules</div>
          {MODULES.map((m) => (
            <button
              key={m.id}
              className={`nav-item ${m.id === active.id ? 'active' : ''}`}
              onClick={() => setActiveId(m.id)}
              aria-current={m.id === active.id ? 'page' : undefined}
            >
              <span className="nav-title">{m.title}</span>
              <span className="nav-desc">{m.description}</span>
            </button>
          ))}
        </nav>
        <main className="content"><ActiveView /></main>
        <ActivityLogPanel />
      </div>
    </div>
  )
}

function UserChip({ me }: { me: ReturnType<typeof useMe> }) {
  const who = me === null ? '…' : me.authenticated ? (me.name || me.email || me.sub) : 'anonymous'
  return (
    <div className="user-chip" title="Signed-in user (gateway identity)">
      <span className="uc-dot" data-on={me?.authenticated ? 'yes' : 'no'} />
      <span className="uc-name">{who}</span>
    </div>
  )
}

// All currently-live tokens, shown as a continuously scrolling marquee that
// loops circularly. Each chip is clickable to make that token active (the active
// one is marked with an accent dot); hovering pauses the scroll so you can read /
// click. Only non-expired tokens appear — expired ones drop out automatically.
function TokenStatus() {
  const { tokens, activeId, setActiveId } = useActiveTokens()
  const live = tokens.filter((t) => !t.expired)

  if (live.length === 0) {
    return (
      <div className="token-status none" title="No live tokens — mint or refresh one in Token Minter">
        <span className="ts-label">Tokens</span>
        <span className="ts-empty">none live</span>
      </div>
    )
  }

  // Render the sequence twice so translateX(-50%) loops seamlessly. Duration
  // scales with the count so each token glides past at a steady pace regardless
  // of how many you hold.
  const seq = [...live, ...live]
  const duration = `${Math.max(14, live.length * 7)}s`
  return (
    <div className="token-status marquee" title="Live tokens — hover to pause, click one to make it active">
      <span className="ts-label">Tokens</span>
      <div className="ts-marquee">
        <div className="ts-track" style={{ animationDuration: duration }}>
          {seq.map((t, i) => {
            const dup = i >= live.length
            return (
              <button
                key={`${i}:${t.id}`}
                type="button"
                className={`ts-chip ${t.id === activeId ? 'active' : ''}`}
                onClick={() => setActiveId(t.id)}
                tabIndex={dup ? -1 : 0}
                aria-hidden={dup || undefined}
                title={`${t.friendly_name} · ${roleLabel(t.role)} · ${t.environment}`}
              >
                <span className="ts-dot" aria-hidden="true" />
                <span className="ts-name">{t.friendly_name}</span>
                <span className="ts-env">{roleLabel(t.role)}</span>
                <span className="ts-env">{t.environment}</span>
                <Countdown expiresAt={t.expires_at} />
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// Live ticking time-to-expiry — the signature readout. Tokens last ~1h; the
// colour shifts ok → warn → crit as expiry approaches.
function Countdown({ expiresAt }: { expiresAt: string }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  const ms = new Date(expiresAt).getTime() - now
  if (ms <= 0) return <span className="countdown expired">EXPIRED</span>
  const m = Math.floor(ms / 60000)
  const s = Math.floor((ms % 60000) / 1000)
  const level = ms < 5 * 60_000 ? 'crit' : ms < 15 * 60_000 ? 'warn' : 'ok'
  return (
    <span className={`countdown ${level}`} aria-label={`Token expires in ${m} minutes ${s} seconds`}>
      {m}:{String(s).padStart(2, '0')}
    </span>
  )
}
